import './App.css';
import './Components/csrfFetch';
import { router, } from "./Pages/index";
import { RouterProvider } from "react-router-dom";
import React, { useEffect, useRef, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import baseURL from './Components/url';
import { guardarSiteConfig, obtenerSiteConfigGuardada } from './Components/siteConfig';

const DEFAULT_BRAND_COLOR = '#780576';
const LEGACY_DEFAULT_COLORS = new Set([
  '#0076e4',
  '#007aff',
  '#0c71cf',
  '#1d4ed8',
  '#2563eb',
  '#3085d6',
  '#3d68b4',
  '#4266b5',
  '#61dafb',
  '#ffc0cb',
  '#920890',
]);

const normalizarColorPrincipal = (color) => {
  const normalizedColor = color?.trim().toLowerCase();

  if (!normalizedColor || LEGACY_DEFAULT_COLORS.has(normalizedColor)) {
    return DEFAULT_BRAND_COLOR;
  }

  return color.trim();
};


function App() {
  const [configLoaded, setConfigLoaded] = useState(false);
  const lastConfigSignatureRef = useRef('');

  useEffect(() => {
    const defaultTitle = document.title;
    const configFieldsForSignature = (config = {}) => ([
      config.nombreTienda?.trim() || '',
      normalizarColorPrincipal(config.colorPrincipal),
      config.logo?.trim() || '',
      config.favicon?.trim() || '',
      config.tituloPestana?.trim() || '',
      String(config.showWhatsappButton ?? ''),
      String(config.storeStatus ?? '')
    ].join('|'));

    const actualizarLink = (selector, rel, href) => {
      let link = document.querySelector(selector);

      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }

      link.setAttribute('href', href);
    };

    const actualizarMeta = (selector, key, keyValue, content) => {
      let meta = document.querySelector(selector);

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(key, keyValue);
        document.head.appendChild(meta);
      }

      if (content) {
        meta.setAttribute('content', content);
      }
    };

    const aplicarConfig = (config) => {
      const colorPrincipal = normalizarColorPrincipal(config.colorPrincipal);
      const configNormalizada = {
        ...config,
        colorPrincipal,
      };

      guardarSiteConfig(configNormalizada, { dispatchEvent: false });
      const storeName = config.nombreTienda?.trim() || 'Tu Tienda';
      const shareTitle = `${storeName} - Tu Catálogo`;
      const browserTitle = config.tituloPestana?.trim() || shareTitle;
      const faviconVersion = encodeURIComponent([
        config.nombreTienda?.trim() || '',
        config.tituloPestana?.trim() || '',
        config.logo?.trim() || '',
        config.favicon?.trim() || '',
        colorPrincipal
      ].join('|'));
      const iconSource = encodeURIComponent(config.logo?.trim() || config.favicon?.trim() || '');
      const faviconHref = `${process.env.PUBLIC_URL}/favicon.ico?v=${faviconVersion}`;
      const metaImage = config.logo?.trim() || config.favicon?.trim() || `${process.env.PUBLIC_URL}/app-icon.php?size=512`;

      document.documentElement.style.setProperty('--color1', colorPrincipal);
      actualizarMeta("meta[name='theme-color']", 'name', 'theme-color', colorPrincipal);

      document.title = browserTitle || defaultTitle;
      actualizarMeta("meta[name='apple-mobile-web-app-title']", 'name', 'apple-mobile-web-app-title', storeName);
      actualizarMeta("meta[name='description']", 'name', 'description', shareTitle);
      actualizarMeta("meta[property='og:title']", 'property', 'og:title', shareTitle);
      actualizarMeta("meta[property='og:description']", 'property', 'og:description', shareTitle);
      actualizarMeta("meta[property='og:site_name']", 'property', 'og:site_name', storeName);
      actualizarMeta("meta[property='og:url']", 'property', 'og:url', window.location.href);
      actualizarMeta("meta[property='og:image']", 'property', 'og:image', metaImage);
      actualizarMeta("meta[property='og:image:secure_url']", 'property', 'og:image:secure_url', metaImage);
      actualizarMeta("meta[name='twitter:card']", 'name', 'twitter:card', 'summary_large_image');
      actualizarMeta("meta[name='twitter:title']", 'name', 'twitter:title', shareTitle);
      actualizarMeta("meta[name='twitter:description']", 'name', 'twitter:description', shareTitle);
      actualizarMeta("meta[name='twitter:image']", 'name', 'twitter:image', metaImage);

      actualizarLink("link[rel='icon']", 'icon', faviconHref);
      actualizarLink("link[rel='shortcut icon']", 'shortcut icon', faviconHref);
      actualizarLink('#apple-touch-icon', 'apple-touch-icon', `${process.env.PUBLIC_URL}/apple-touch-icon.png?v=${faviconVersion}`);
      actualizarLink("link[rel='apple-touch-icon-precomposed']", 'apple-touch-icon-precomposed', `${process.env.PUBLIC_URL}/apple-touch-icon.png?v=${faviconVersion}`);
      actualizarLink("link[rel='manifest']", 'manifest', `${process.env.PUBLIC_URL}/manifest.php?logo=${iconSource}&v=${faviconVersion}`);
    };

    const configGuardada = obtenerSiteConfigGuardada();
    if (configGuardada) {
      aplicarConfig(configGuardada);
    }

    const fetchConfig = async ({ reloadOnChange = false } = {}) => {
      try {
        const response = await fetch(`${baseURL}/configGet.php?ts=${Date.now()}`, {
          cache: 'no-store'
        });
        const data = await response.json();
        if (data.config) {
          const nextSignature = configFieldsForSignature(data.config);
          const previousSignature = lastConfigSignatureRef.current;

          aplicarConfig(data.config);
          lastConfigSignatureRef.current = nextSignature;

          if (reloadOnChange && previousSignature && previousSignature !== nextSignature) {
            window.location.reload();
            return;
          }
        }
      } catch (error) {
        console.error('Error al cargar configuración global:', error);
      } finally {
        setConfigLoaded(true);
      }
    };

    const syncConfigWhenVisible = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      fetchConfig({ reloadOnChange: true });
    };

    const syncConfigFromStorage = (event) => {
      if (event?.key && event.key !== 'siteConfig') {
        return;
      }

      const nextConfig = event?.detail || obtenerSiteConfigGuardada();
      if (!nextConfig) {
        return;
      }

      const nextSignature = configFieldsForSignature(nextConfig);
      aplicarConfig(nextConfig);
      lastConfigSignatureRef.current = nextSignature;
    };

    fetchConfig();
    window.addEventListener('focus', syncConfigWhenVisible);
    window.addEventListener('pageshow', syncConfigWhenVisible);
    window.addEventListener('storage', syncConfigFromStorage);
    window.addEventListener('site-config-updated', syncConfigFromStorage);
    document.addEventListener('visibilitychange', syncConfigWhenVisible);

    return () => {
      window.removeEventListener('focus', syncConfigWhenVisible);
      window.removeEventListener('pageshow', syncConfigWhenVisible);
      window.removeEventListener('storage', syncConfigFromStorage);
      window.removeEventListener('site-config-updated', syncConfigFromStorage);
      document.removeEventListener('visibilitychange', syncConfigWhenVisible);
    };
  }, []);

  if (!configLoaded) {
    return null; // Evita renderizar y mostrar el color anterior mientras carga
  }

  return (
    <>
      <RouterProvider router={router} />
      <ToastContainer />
    </>
  );
}

export default App;
